import React, { useMemo, useRef } from 'react';
import type { Chart as ChartJS, ChartOptions } from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { AzureCostDailyPoint } from '../../../types/azureCost';

function formatDayLabel(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
}

function formatAxisUsd(value: number): string {
  if (value >= 1000) return `$${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}K`;
  return `$${Math.round(value)}`;
}

function splitActualAndForecast(series: AzureCostDailyPoint[]): {
  labels: string[];
  actual: (number | null)[];
  forecast: (number | null)[];
} {
  const labels = series.map((p) => formatDayLabel(p.date));
  const lastActualIndex = series.reduce(
    (last, p, i) => (!p.isForecast ? i : last),
    -1
  );

  const actual = series.map((p) => (p.isForecast ? null : p.accumulatedCost));
  const forecast = series.map((p, i) => {
    if (p.isForecast) return p.accumulatedCost;
    if (i === lastActualIndex) return p.accumulatedCost;
    return null;
  });

  return { labels, actual, forecast };
}

export interface AzureCostAreaChartProps {
  dailySeries: AzureCostDailyPoint[];
  currency: string;
  className?: string;
}

const AzureCostAreaChart: React.FC<AzureCostAreaChartProps> = ({
  dailySeries,
  currency,
  className = '',
}) => {
  const chartRef = useRef<ChartJS<'line'>>(null);
  const { labels, actual, forecast } = useMemo(
    () => splitActualAndForecast(dailySeries),
    [dailySeries]
  );

  const maxY = useMemo(() => {
    const vals = dailySeries.map((p) => p.accumulatedCost);
    const top = Math.max(0, ...vals);
    return top > 0 ? top * 1.12 : 100;
  }, [dailySeries]);

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            boxWidth: 8,
            padding: 16,
            font: { size: 12 },
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              if (v == null) return '';
              return `${ctx.dataset.label}: ${new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency,
                minimumFractionDigits: 2,
              }).format(v)}`;
            },
          },
        },
        datalabels: { display: false },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 12,
            font: { size: 11 },
            color: '#64748b',
          },
        },
        y: {
          min: 0,
          max: maxY,
          grid: { color: 'rgba(148, 163, 184, 0.2)' },
          ticks: {
            callback: (v) => formatAxisUsd(Number(v)),
            font: { size: 11 },
            color: '#64748b',
          },
        },
      },
    }),
    [currency, maxY]
  );

  if (!dailySeries.length) {
    return (
      <div
        className={`flex min-h-[320px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/50 text-sm text-slate-500 ${className}`}
      >
        No daily cost data for this period.
      </div>
    );
  }

  return (
    <div className={`relative min-h-[320px] w-full ${className}`}>
      <Line
        ref={chartRef}
        options={options}
        data={{
          labels,
          datasets: [
            {
              label: 'Accumulated cost',
              data: actual,
              borderColor: '#107c10',
              backgroundColor: 'rgba(16, 124, 16, 0.35)',
              fill: true,
              tension: 0.2,
              pointRadius: 0,
              pointHoverRadius: 4,
              borderWidth: 2,
              spanGaps: false,
            },
            {
              label: 'Forecast cost',
              data: forecast,
              borderColor: 'rgba(16, 124, 16, 0.45)',
              backgroundColor: 'rgba(16, 124, 16, 0.12)',
              fill: true,
              tension: 0.2,
              pointRadius: 0,
              pointHoverRadius: 4,
              borderWidth: 2,
              borderDash: [4, 3],
              spanGaps: false,
            },
          ],
        }}
      />
    </div>
  );
};

export default AzureCostAreaChart;
